import React, { useState, useCallback, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { Plus, X, Check, AlertCircle, Mail, Building2 } from 'lucide-react';
import api from '../api';

const InlineVendorPicker = ({ 
  line, 
  initialValue = '',
  onSelected, 
  onCancel, 
  position = 'bottom',
  width = 'auto'
}) => {
  const [searchTerm, setSearchTerm] = useState(initialValue);
  const [suggestions, setSuggestions] = useState([]);
  const [activeIndex, setActiveIndex] = useState(-1);
  const [loading, setLoading] = useState(false);
  const [creating, setCreating] = useState(false);
  const [error, setError] = useState('');
  const [showCreateForm, setShowCreateForm] = useState(false);
  
  const [newVendor, setNewVendor] = useState({
    name: '',
    email: '',
    phone: '',
    price: '',
    lead_time: ''
  });
  
  const [announcement, setAnnouncement] = useState('');
  const [dropdownPosition, setDropdownPosition] = useState({ top: 0, left: 0, width: 0, showAbove: false });
  
  const containerRef = useRef(null);
  const inputRef = useRef(null);
  const dropdownRef = useRef(null);
  const debounceRef = useRef(null);
  
  // Search vendors with debounce
  const searchVendors = useCallback(async (term) => {
    if (!term || term.length < 2) {
      setSuggestions([]);
      setShowCreateForm(false);
      return;
    }
    
    setLoading(true);
    setError('');
    
    try {
      const response = await api.get(`/api/vendors/search?term=${encodeURIComponent(term)}`);
      
      console.log('Vendor search response:', response.data);
      
      if (response.data.success) {
        setSuggestions(response.data.vendors || []);
        setActiveIndex(-1);
        setShowCreateForm(response.data.vendors.length === 0);
      } else {
        setError(response.data.error || 'Search failed');
        setSuggestions([]);
        setShowCreateForm(true);
      }
    } catch (err) {
      console.error('Vendor search error:', err);
      if (err.response?.status === 401) {
        setError('Authentication required. Please login again.');
      } else {
        setError('Search failed. Please try again.');
      }
      setSuggestions([]);
      setShowCreateForm(true);
    } finally {
      setLoading(false);
    }
  }, []);
  
  // Handle input changes with debounce
  const handleInputChange = useCallback((e) => {
    const value = e.target.value;
    setSearchTerm(value);
    setError('');
    setNewVendor(prev => ({ ...prev, name: value }));
    
    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
    }
    
    debounceRef.current = setTimeout(() => {
      searchVendors(value);
    }, 300);
  }, [searchVendors]);
  
  // Select existing vendor
  const selectVendor = useCallback(async (vendor) => {
    try {
      setLoading(true);
      
      const vendorData = {
        vendor_id: vendor.id,
        vendor_name: vendor.name,
        // Note: price and lead_time are not provided by search API
        // They will be set separately by the user or from product vendor info
      };
      
      onSelected(line.id, vendorData);
      
    } catch (err) {
      setError('Failed to select vendor. Please try again.');
    } finally {
      setLoading(false);
    }
  }, [line.id, onSelected]);
  
  // Create new vendor
  const createVendor = useCallback(async () => {
    if (!newVendor.name.trim()) {
      setError('Vendor name is required');
      return;
    }
    
    setCreating(true);
    setError('');
    
    try {
      const response = await api.post('/api/vendors', {
        name: newVendor.name.trim(),
        email: newVendor.email.trim() || undefined,
        phone: newVendor.phone.trim() || undefined,
      });
      
      console.log('Create vendor response:', response.data);
      
      if (response.data.success) {
        const vendorData = {
          vendor_id: response.data.vendor.id,
          vendor_name: response.data.vendor.name,
          // Price and lead time will be handled separately if provided
          ...(newVendor.price && { price_unit: parseFloat(newVendor.price) }),
          ...(newVendor.lead_time && { lead_time: parseInt(newVendor.lead_time) })
        };
        
        onSelected(line.id, vendorData);
      } else {
        setError(response.data.error || 'Failed to create vendor');
      }
    } catch (err) {
      console.error('Create vendor error:', err);
      if (err.response?.status === 401) {
        setError('Authentication required. Please login again.');
      } else {
        setError('Failed to create vendor. Please try again.');
      }
    } finally {
      setCreating(false);
    }
  }, [newVendor, line.id, onSelected]);
  
  // Enhanced keyboard navigation for listbox
  const handleKeyDown = useCallback((e) => {
    // If we're in the input field, handle navigation differently
    if (e.target === inputRef.current) {
      switch (e.key) {
        case 'ArrowDown':
          e.preventDefault();
          if (suggestions.length > 0 && !showCreateForm) {
            setActiveIndex(0);
            // Move focus to listbox
            if (dropdownRef.current) {
              const firstOption = dropdownRef.current.querySelector('[role="option"]');
              if (firstOption) firstOption.focus();
            }
          }
          break;
          
        case 'ArrowUp':
          e.preventDefault();
          if (suggestions.length > 0 && !showCreateForm) {
            setActiveIndex(suggestions.length - 1);
            // Move focus to listbox
            if (dropdownRef.current) {
              const lastOption = dropdownRef.current.querySelector('[role="option"]:last-child');
              if (lastOption) lastOption.focus();
            }
          }
          break;
          
        case 'Enter':
          e.preventDefault();
          if (showCreateForm) {
            createVendor();
          } else if (activeIndex >= 0 && activeIndex < suggestions.length) {
            // Select the currently active/highlighted vendor
            selectVendor(suggestions[activeIndex]);
          } else if (activeIndex === suggestions.length) {
            // "Create new vendor" option is selected
            setShowCreateForm(true);
          } else if (suggestions.length === 1) {
            // Only one suggestion, select it
            selectVendor(suggestions[0]);
          } else if (suggestions.length === 0 && searchTerm.trim()) {
            setShowCreateForm(true);
          }
          break;
          
        case 'Escape':
          e.preventDefault();
          onCancel();
          break;
          
        default:
          // Let other keys pass through to input for typing
          break;
      }
    } else {
      // Handle navigation within listbox
      switch (e.key) {
        case 'ArrowDown':
          e.preventDefault();
          if (showCreateForm) return;
          // Include "create new" option in navigation (index = suggestions.length)
          const totalOptions = suggestions.length + 1;
          const nextIndex = activeIndex < totalOptions - 1 ? activeIndex + 1 : 0; // Wrap around
          setActiveIndex(nextIndex);
          // Focus the next option
          const nextOption = e.currentTarget.parentElement.children[nextIndex];
          if (nextOption) nextOption.focus();
          break;
          
        case 'ArrowUp':
          e.preventDefault();
          if (showCreateForm) return;
          // Include "create new" option in navigation
          const totalOptionsUp = suggestions.length + 1;
          const prevIndex = activeIndex > 0 ? activeIndex - 1 : totalOptionsUp - 1; // Wrap around
          setActiveIndex(prevIndex);
          // Focus the previous option
          const prevOption = e.currentTarget.parentElement.children[prevIndex];
          if (prevOption) prevOption.focus();
          break;
          
        case 'Home':
          e.preventDefault();
          if (suggestions.length > 0) {
            setActiveIndex(0);
            const firstOption = e.currentTarget.parentElement.children[0];
            if (firstOption) firstOption.focus();
          }
          break;
          
        case 'End':
          e.preventDefault();
          if (suggestions.length > 0) {
            // Set to "create new" option (last option)
            const lastIndex = suggestions.length;
            setActiveIndex(lastIndex);
            const lastOption = e.currentTarget.parentElement.children[lastIndex];
            if (lastOption) lastOption.focus();
          }
          break;
          
        case 'Enter':
        case ' ':
          e.preventDefault();
          if (activeIndex >= 0 && activeIndex < suggestions.length) {
            // Selecting a vendor
            selectVendor(suggestions[activeIndex]);
          } else if (activeIndex === suggestions.length) {
            // Selecting "create new" option
            setShowCreateForm(true);
          }
          break;
          
        case 'Escape':
          e.preventDefault();
          // Return focus to input
          if (inputRef.current) {
            inputRef.current.focus();
          }
          break;
          
        case 'Tab':
          // Allow normal tab behavior to exit the component
          onCancel();
          break;
          
        default:
          // For typing, return focus to input
          if (e.key.length === 1 && inputRef.current) {
            inputRef.current.focus();
            // Let the input handle the character
            inputRef.current.value += e.key;
            handleInputChange({ target: inputRef.current });
          }
          break;
      }
    }
  }, [suggestions, activeIndex, selectVendor, onCancel, showCreateForm, createVendor, searchTerm, handleInputChange]);
  
  // Calculate dropdown position for portal (using viewport coordinates for fixed positioning)
  const updateDropdownPosition = useCallback(() => {
    if (inputRef.current) {
      const rect = inputRef.current.getBoundingClientRect();
      const viewportHeight = window.innerHeight;
      const spaceBelow = viewportHeight - rect.bottom;
      const spaceAbove = rect.top;
      const dropdownMaxHeight = 300; // maxHeight from CSS
      const minSpaceNeeded = Math.min(dropdownMaxHeight, 250); // Minimum space needed
      
      // Show above if not enough space below but enough space above
      const showAbove = spaceBelow < minSpaceNeeded && spaceAbove > spaceBelow;
      
      // For position: fixed, use viewport coordinates directly (no scroll offset needed)
      setDropdownPosition({
        top: showAbove ? rect.top : rect.bottom,
        left: rect.left,
        width: rect.width,
        showAbove: showAbove
      });
    }
  }, []);

  // Outside click handler - check both container and dropdown (portal)
  useEffect(() => {
    const handleClickOutside = (e) => {
      // Check if click is inside the container (input field) or the dropdown (portal)
      const isInsideContainer = containerRef.current && containerRef.current.contains(e.target);
      const isInsideDropdown = dropdownRef.current && dropdownRef.current.contains(e.target);
      
      // Only cancel if click is outside both container and dropdown
      if (!isInsideContainer && !isInsideDropdown) {
        onCancel();
      }
    };
    
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [onCancel]);
  
  // Focus input on mount and update position
  useEffect(() => {
    if (inputRef.current) {
      inputRef.current.focus();
      // Select all text if there's an initial value
      if (initialValue) {
        inputRef.current.select();
      }
      // Calculate initial position
      updateDropdownPosition();
    }
  }, [initialValue, updateDropdownPosition]);
  
  // Announce active option changes for screen readers
  useEffect(() => {
    if (activeIndex >= 0 && activeIndex < suggestions.length) {
      const vendor = suggestions[activeIndex];
      setAnnouncement(`${vendor.name}${vendor.email ? `, ${vendor.email}` : ''}. Option ${activeIndex + 1} of ${suggestions.length + 1}.`);
    } else if (activeIndex === suggestions.length) {
      setAnnouncement(`Create new vendor "${searchTerm}". Option ${activeIndex + 1} of ${suggestions.length + 1}.`);
    } else {
      setAnnouncement('');
    }
  }, [activeIndex, suggestions, searchTerm]);

  // Cleanup debounce
  useEffect(() => {
    return () => {
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
      }
    };
  }, []);

  // Calculate position immediately when create form is shown
  useEffect(() => {
    if (showCreateForm && inputRef.current) {
      updateDropdownPosition();
    }
  }, [showCreateForm, updateDropdownPosition]);

  // Update position when dropdown should be shown
  useEffect(() => {
    if ((searchTerm.length >= 2 || showCreateForm) && inputRef.current) {
      // Initial position calculation
      updateDropdownPosition();
      
      // Use requestAnimationFrame for smooth updates during scroll
      let rafId = null;
      const handleScroll = () => {
        if (rafId) cancelAnimationFrame(rafId);
        rafId = requestAnimationFrame(() => {
          updateDropdownPosition();
        });
      };
      
      const handleResize = () => {
        updateDropdownPosition();
      };
      
      // Listen to scroll on all scrollable containers (table, window, etc.)
      window.addEventListener('scroll', handleScroll, true);
      window.addEventListener('resize', handleResize);
      
      // Also listen to scroll on the table container if it exists
      const tableContainer = inputRef.current.closest('.table-container');
      if (tableContainer) {
        tableContainer.addEventListener('scroll', handleScroll, true);
      }
      
      return () => {
        if (rafId) cancelAnimationFrame(rafId);
        window.removeEventListener('scroll', handleScroll, true);
        window.removeEventListener('resize', handleResize);
        if (tableContainer) {
          tableContainer.removeEventListener('scroll', handleScroll, true);
        }
      };
    }
  }, [searchTerm, showCreateForm, updateDropdownPosition]);
  
  // Calculate dropdown style for portal (fixed positioning)
  const getDropdownStyle = () => {
    // Use calculated width or fallback to input width or minimum width
    const dropdownWidth = dropdownPosition.width > 0 
      ? dropdownPosition.width 
      : (inputRef.current?.offsetWidth || (width === 'auto' ? 280 : parseInt(width)));
    
    // Get current input position
    const rect = inputRef.current?.getBoundingClientRect();
    const inputTop = rect?.top || 0;
    const inputBottom = rect?.bottom || 0;
    
    const baseStyle = {
      position: 'fixed',
      left: `${dropdownPosition.left || (rect?.left || 0)}px`,
      width: `${dropdownWidth}px`,
      background: 'white',
      border: '1px solid #e2e8f0',
      borderRadius: '8px',
      boxShadow: '0 10px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)',
      zIndex: 10000,
      maxHeight: '300px',
      overflowY: 'auto',
      minWidth: width === 'auto' ? '280px' : width
    };
    
    // Position above or below based on available space
    if (dropdownPosition.showAbove) {
      // Position above: use bottom to align with top of input
      baseStyle.bottom = `${window.innerHeight - inputTop}px`;
    } else {
      // Position below: use top to align with bottom of input
      baseStyle.top = `${inputBottom}px`;
    }
    
    return baseStyle;
  };
  
  return (
    <div ref={containerRef} className="inline-vendor-picker" style={{ position: 'relative', width: '100%', zIndex: 10000 }}>
      {/* Input Field with ARIA attributes */}
      <input
        ref={inputRef}
        type="text"
        value={searchTerm}
        onChange={handleInputChange}
        onKeyDown={handleKeyDown}
        placeholder="Type vendor name..."
        role="combobox"
        aria-expanded={(searchTerm.length >= 2 || showCreateForm)}
        aria-haspopup="listbox"
        aria-controls={`vendor-listbox-${line.id}`}
        aria-owns={`vendor-listbox-${line.id}`}
        aria-activedescendant={activeIndex >= 0 ? `vendor-option-${line.id}-${activeIndex}` : undefined}
        aria-autocomplete="list"
        aria-label="Search for vendor"
        style={{
          width: '100%',
          padding: '8px 12px',
          border: '2px solid #3b82f6',
          borderRadius: '6px',
          fontSize: '14px',
          outline: 'none',
          boxShadow: '0 0 0 3px rgba(59, 130, 246, 0.1)',
          background: 'white'
        }}
      />
      
      {/* Dropdown - Rendered via Portal to ensure it's above table */}
      {(searchTerm.length >= 2 || showCreateForm) && (dropdownPosition.width > 0 || showCreateForm) && createPortal(
        <div ref={dropdownRef} style={getDropdownStyle()}>
          
          {/* Search Results */}
          {!showCreateForm && searchTerm.length >= 2 && (
            <>
              {loading ? (
                <div style={{ padding: '16px', textAlign: 'center' }}>
                  <div style={{
                    width: '20px',
                    height: '20px',
                    border: '2px solid #e5e7eb',
                    borderTop: '2px solidrgb(95, 95, 96)',
                    borderRadius: '50%',
                    animation: 'spin 1s linear infinite',
                    margin: '0 auto 8px'
                  }} />
                  <span style={{ fontSize: '12px', color: '#6b7280' }}>Searching...</span>
                </div>
              ) : suggestions.length === 0 ? (
                <div style={{ padding: '16px', textAlign: 'center', zIndex: 1000 }}>
                  <Building2 size={24} style={{ color: '#9ca3af', marginBottom: '8px' }} />
                  <div style={{ fontSize: '14px', color: '#374151', marginBottom: '4px' }}>
                    No vendors found
                  </div>
                  <button
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      setShowCreateForm(true);
                    }}
                    onMouseDown={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                    }}
                    style={{
                      fontSize: '12px',
                      color: '#3b82f6',
                      background: 'none',
                      border: 'none',
                      cursor: 'pointer',
                      textDecoration: 'underline',
                      pointerEvents: 'auto'
                    }}
                  >
                    Create new vendor
                  </button>
                </div>
              ) : (
                <div
                  role="listbox"
                  id={`vendor-listbox-${line.id}`}
                  aria-label="Vendor options"
                  style={{
                    maxHeight: '256px',
                    overflowY: 'auto'
                  }}
                >
                  {suggestions.map((vendor, index) => (
                    <div
                      key={vendor.id}
                      role="option"
                      id={`vendor-option-${line.id}-${index}`}
                      tabIndex={index === activeIndex ? 0 : -1}
                      aria-selected={index === activeIndex}
                      onClick={(e) => {
                        e.stopPropagation();
                        setActiveIndex(index);
                        selectVendor(vendor);
                      }}
                      onMouseEnter={() => setActiveIndex(index)}
                      onFocus={() => setActiveIndex(index)}
                      onKeyDown={handleKeyDown}
                      style={{
                        padding: '12px 16px',
                        cursor: 'pointer',
                        borderBottom: index < suggestions.length - 1 ? '1px solid #f1f5f9' : 'none',
                        background: index === activeIndex ? '#eff6ff' : 'transparent',
                        borderLeft: index === activeIndex ? '3px solid #3b82f6' : '3px solid transparent',
                        transition: 'all 0.15s ease',
                        outline: index === activeIndex ? '2px solid #3b82f6' : 'none',
                        outlineOffset: '-2px'
                      }}
                    >
                      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                        <div style={{ flex: 1 }}>
                          <div style={{ 
                            fontWeight: '600', 
                            color: '#111827',
                            marginBottom: '4px',
                            fontSize: '14px'
                          }}>
                            {vendor.name}
                          </div>
                          
                          <div style={{
                            display: 'flex',
                            gap: '12px',
                            fontSize: '12px',
                            color: '#6b7280',
                            flexWrap: 'wrap'
                          }}>
                            {vendor.email && (
                              <span style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                                <Mail size={10} />
                                {vendor.email}
                              </span>
                            )}
                            {vendor.phone && (
                              <span style={{ 
                                fontSize: '12px',
                                color: '#6b7280'
                              }}>
                                📞 {vendor.phone}
                              </span>
                            )}
                          </div>
                        </div>
                        
                        <div style={{
                          fontSize: '11px',
                          color: '#3b82f6',
                          fontWeight: '500',
                          padding: '2px 8px',
                          background: '#eff6ff',
                          borderRadius: '12px'
                        }}>
                          {index === activeIndex ? 'Selected' : 'Select'}
                        </div>
                      </div>
                    </div>
                  ))}
                  
                  {/* Option to create new */}
                  <div
                    role="option"
                    id={`vendor-option-${line.id}-create`}
                    tabIndex={activeIndex === suggestions.length ? 0 : -1}
                    aria-selected={activeIndex === suggestions.length}
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      setActiveIndex(suggestions.length);
                      setShowCreateForm(true);
                    }}
                    onMouseDown={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                    }}
                    onMouseEnter={() => setActiveIndex(suggestions.length)}
                    onFocus={() => setActiveIndex(suggestions.length)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        e.stopPropagation();
                        setShowCreateForm(true);
                      } else {
                        handleKeyDown(e);
                      }
                    }}
                    style={{
                      padding: '12px 16px',
                      cursor: 'pointer',
                      borderTop: '1px solid #f1f5f9',
                      background: activeIndex === suggestions.length ? '#dcfce7' : '#f8fafc',
                      color: '#16a34a',
                      fontSize: '13px',
                      fontWeight: '500',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px',
                      outline: activeIndex === suggestions.length ? '2px solid #16a34a' : 'none',
                      outlineOffset: '-2px',
                      transition: 'all 0.15s ease'
                    }}
                  >
                    <Plus size={14} />
                    Create new vendor "{searchTerm}"
                  </div>
                </div>
              )}
            </>
          )}
          
          {/* Create Form */}
          {showCreateForm && (
            <div style={{ padding: '16px' }}>
              <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                marginBottom: '16px',
                paddingBottom: '8px',
                borderBottom: '1px solid #e2e8f0'
              }}>
                <Plus size={16} style={{ color: '#16a34a' }} />
                <span style={{ fontWeight: '600', color: '#111827', fontSize: '14px' }}>
                  Create New Vendor
                </span>
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    setShowCreateForm(false);
                  }}
                  style={{
                    marginLeft: 'auto',
                    background: 'none',
                    border: 'none',
                    cursor: 'pointer',
                    padding: '4px',
                    borderRadius: '4px'
                  }}
                >
                  <X size={14} style={{ color: '#6b7280' }} />
                </button>
              </div>
              
              <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                <input
                  type="text"
                  value={newVendor.name}
                  onChange={(e) => setNewVendor(prev => ({ ...prev, name: e.target.value }))}
                  placeholder="Vendor name *"
                  style={{
                    padding: '8px 12px',
                    border: '1px solid #d1d5db',
                    borderRadius: '6px',
                    fontSize: '13px',
                    outline: 'none'
                  }}
                />
                
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px' }}>
                  <input
                    type="email"
                    value={newVendor.email}
                    onChange={(e) => setNewVendor(prev => ({ ...prev, email: e.target.value }))}
                    placeholder="Email"
                    style={{
                      padding: '8px 12px',
                      border: '1px solid #d1d5db',
                      borderRadius: '6px',
                      fontSize: '13px',
                      outline: 'none'
                    }}
                  />
                  
                  <input
                    type="tel"
                    value={newVendor.phone}
                    onChange={(e) => setNewVendor(prev => ({ ...prev, phone: e.target.value }))}
                    placeholder="Phone"
                    style={{
                      padding: '8px 12px',
                      border: '1px solid #d1d5db',
                      borderRadius: '6px',
                      fontSize: '13px',
                      outline: 'none'
                    }}
                  />
                </div>
                
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px' }}>
                  <input
                    type="number"
                    step="0.01"
                    value={newVendor.price}
                    onChange={(e) => setNewVendor(prev => ({ ...prev, price: e.target.value }))}
                    placeholder="Price"
                    style={{
                      padding: '8px 12px',
                      border: '1px solid #d1d5db',
                      borderRadius: '6px',
                      fontSize: '13px',
                      outline: 'none'
                    }}
                  />
                  
                  <input
                    type="number"
                    value={newVendor.lead_time}
                    onChange={(e) => setNewVendor(prev => ({ ...prev, lead_time: e.target.value }))}
                    placeholder="Lead time (days)"
                    style={{
                      padding: '8px 12px',
                      border: '1px solid #d1d5db',
                      borderRadius: '6px',
                      fontSize: '13px',
                      outline: 'none'
                    }}
                  />
                </div>
                
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    createVendor();
                  }}
                  disabled={creating || !newVendor.name.trim()}
                  style={{
                    padding: '8px 16px',
                    background: creating || !newVendor.name.trim() ? '#9ca3af' : '#16a34a',
                    color: 'white',
                    border: 'none',
                    borderRadius: '6px',
                    fontSize: '13px',
                    fontWeight: '500',
                    cursor: creating || !newVendor.name.trim() ? 'not-allowed' : 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '6px'
                  }}
                >
                  {creating ? (
                    <>
                      <div style={{
                        width: '12px',
                        height: '12px',
                        border: '2px solid rgba(255,255,255,0.3)',
                        borderTop: '2px solid white',
                        borderRadius: '50%',
                        animation: 'spin 1s linear infinite'
                      }} />
                      Creating...
                    </>
                  ) : (
                    <>
                      <Check size={14} />
                      Create & Select
                    </>
                  )}
                </button>
              </div>
            </div>
          )}
          
          {/* Error Message */}
          {error && (
            <div style={{
              padding: '12px 16px',
              background: '#fee2e2',
              borderTop: '1px solid #fecaca',
              display: 'flex',
              alignItems: 'center',
              gap: '8px'
            }}>
              <AlertCircle size={14} style={{ color: '#dc2626', flexShrink: 0 }} />
              <span style={{ color: '#dc2626', fontSize: '12px' }}>
                {error}
              </span>
            </div>
          )}
        </div>,
        document.body
      )}
      
      {/* Screen reader live region */}
      <div 
        aria-live="polite" 
        aria-atomic="true"
        style={{ 
          position: 'absolute',
          left: '-10000px',
          top: 'auto',
          width: '1px',
          height: '1px',
          overflow: 'hidden'
        }}
      >
        {announcement}
      </div>

      {/* Global styles for animations */}
      <style jsx>{`
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );
};

export default InlineVendorPicker;